/**
 * This class wraps a function so that we can defer executing the function by X
 * seconds.
 */
class DelayedTick {
  private readonly onTick: Function;
  private cancelPending: Function | null;
  constructor(onTick: Function) {
    this.onTick = onTick;
    this.cancelPending = null;
  }

  /**
   * Call |onTick| after |delayInSeconds| has elapsed. If there is already a
   * pending call to |onTick|, the pending call will be canceled.
   */
  tickAfter(delayInSeconds: number) {
    // We only want one timeout set at a time, so make sure no other timeouts
    // are running.
    this.stop();

    // We will wrap these values in a function to allow us to cancel the timeout
    // we are about to create.
    let alive = true;
    let timeoutId: number;

    this.cancelPending = () => {
      window.clearTimeout(timeoutId);
      timeoutId = 0;
      alive = false;
    };

    // We will wrap the callback so that we can double-check our
    // |alive| flag.
    const onTick = () => {
      if (alive) {
        this.onTick();
      }
    };

    timeoutId = window.setTimeout(onTick, delayInSeconds * 1000);

    return this;
  }

  /**
   * Cancel any pending calls to |onTick|. If there are no pending calls to
   * |onTick|, this will be a no-op.
   */
  stop() {
    if (this.cancelPending) {
      this.cancelPending();
      this.cancelPending = null;
    }
  }
}

/**
 * A timer allows a single function to be executed at a later time or at
 * regular intervals.
 */
export class Timer {
  private readonly onTick: Function;
  private ticker: DelayedTick | null;
  /**
   * Create a new timer. A timer is committed to a single callback function.
   * While there is no technical reason to do this, it is far easier to
   * understand and use timers when they are connected to one functional idea.
   */
  constructor(onTick: Function) {
    /**
     * Each time our timer "does work", we call that a "tick". The name comes
     * from old analog clocks.
     */
    this.onTick = onTick;
    this.ticker = null;
  }

  /**
   * Have the timer call |onTick| now.
   */
  tickNow() {
    this.stop();
    this.onTick();

    return this;
  }

  /**
   * Have the timer call |onTick| after |seconds| has elapsed unless |stop| is
   * called first.
   */
  tickAfter(seconds: number) {
    this.stop();

    this.ticker = new DelayedTick(() => {
      this.onTick();
    }).tickAfter(seconds);

    return this;
  }

  /**
   * Have the timer call |onTick| every |seconds| until |stop| is called.
   */
  tickEvery(seconds: number) {
    this.stop();

    this.ticker = new DelayedTick(() => {
      // Schedule the timer again first. |onTick_| could cancel the timer and
      // rescheduling first simplifies the implementation.
      this.ticker?.tickAfter(seconds);
      this.onTick();
    }).tickAfter(seconds);

    return this;
  }

  /**
   * Stop the timer and clear the previous behaviour. The timer is still usable
   * after calling |stop|.
   */
  stop() {
    if (this.ticker) {
      this.ticker.stop();
      this.ticker = null;
    }
  }
}
